import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { paletteFromTitle } from '@shared/mappers';
import type { SharePreview } from '@shared/types';

import { useAuth } from '@/auth/authContext';
import { BrandMark } from '@/components/BrandMark';
import { PaletteAvatar } from '@/components/PaletteAvatar';
import { PerspectiveCover } from '@/components/PerspectiveCover';
import { Poster } from '@/components/Poster';
import { Reveal, RevealGroup } from '@/components/Reveal';
import { StateBlock } from '@/components/StateBlock';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocale } from '@/i18n/locale';
import { localized } from '@/i18n/localized';

import { useSharePreview } from './api';
import { appSchemeLink, isValidShareCode, shareTargetPath } from './route';

/**
 * `/s/:code` — the public preview page (docs/sharing-plan.md §4, §5.3): the
 * SPA fallback for the same preview the server-rendered `screenatlas.app/s/*`
 * page produces (§5.4). Renders OUTSIDE `<RequireAuth>` and the app shell — a
 * logged-out visitor must be able to see the card. Works entirely off the
 * public `GET /v1/shares/:code` resolver; no session is required to load it.
 */
export function SharePreviewScreen() {
  const { code = '' } = useParams<{ code: string }>();
  const valid = isValidShareCode(code);
  const query = useSharePreview(valid ? code : undefined);
  const { t } = useLocale();

  const result = query.data;
  const preview = result && !('status' in result) ? result : null;

  let body: ReactNode;
  if (!valid) {
    body = <Unavailable />;
  } else if (query.isPending) {
    body = <PreviewSkeleton />;
  } else if (query.isError || !preview) {
    body = <Unavailable />;
  } else {
    body = <Preview code={code} preview={preview} />;
  }

  return (
    <div className="min-h-dvh bg-bg">
      <header className="px-4 py-5 sm:px-6">
        <Link to="/" aria-label={t('auth.homeLink')} className="inline-flex rounded">
          <BrandMark size="sm" />
        </Link>
      </header>
      <main className="mx-auto flex max-w-3xl flex-col px-4 pb-16 sm:px-6">{body}</main>
    </div>
  );
}

/* The hero case's box, shared with the skeleton so the first open never shifts. */
const CASE = 'w-[168px] shrink-0 sm:w-[220px]';
const CASE_SIZES = '(min-width: 640px) 220px, 168px';
const HERO = 'flex flex-col items-center gap-8 py-6 sm:flex-row sm:items-center sm:gap-12 sm:py-12';
const COPY = 'flex min-w-0 flex-1 flex-col items-center gap-3 text-center sm:items-start sm:text-left';

function PreviewSkeleton() {
  const { t } = useLocale();
  return (
    <div role="status" aria-label={t('show.loading')} className={HERO}>
      <Skeleton className={`aspect-[2/3] rounded-sm ${CASE}`} />
      <div className={`${COPY} w-full`}>
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-9 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="mt-3 h-12 w-full rounded sm:w-64" />
      </div>
    </div>
  );
}

function Unavailable() {
  const { t } = useLocale();
  return (
    <StateBlock
      role="status"
      overline={t('share.preview.overline')}
      title={t('share.unavailable')}
      body={t('share.unavailableBody')}
      action={
        <Button asChild variant="neutral">
          <Link to="/">{t('share.goToTonight')}</Link>
        </Button>
      }
    />
  );
}

/**
 * The shared title as the hero (ui-ux.md §6 "Perspective cover"): the poster
 * is a `rest` case that swings in (`Reveal kind="case"`), then the copy rises
 * line by line and the call to action lands last — one `RevealGroup`. A
 * signed-in visitor gets one amber "Open in ScreenAtlas"; a visitor without a
 * session is invited to join first (Create account amber, Log in quiet), both
 * carrying `next` back to the title. "Open in app" is always a plain link.
 */
function Preview({ code, preview }: { code: string; preview: SharePreview }) {
  const { t, locale } = useLocale();
  const { status, isAuthenticated } = useAuth();
  const name = localized(preview.title.title, preview.title.titleBr, locale);
  const synopsis = localized(preview.title.synopsis ?? '', preview.title.synopsisBr, locale);
  const targetPath = shareTargetPath(preview.titleType, preview.tmdbId, preview.code);
  const sharerName = preview.sharer ? (preview.sharer.displayName ?? preview.sharer.username) : null;
  const meta = [preview.title.year, preview.title.genres.join(', ')].filter(Boolean).join(' · ');
  const next = targetPath ? encodeURIComponent(targetPath) : '';

  return (
    <RevealGroup id="share-preview">
      <article aria-label={name} className={HERO}>
        <Reveal kind="case" order={1} className={CASE}>
          <PerspectiveCover variant="rest">
            <Poster
              id={preview.title.tmdbId}
              name={name}
              palette={preview.title.palette}
              imageUrl={preview.title.posterUrl}
              imageFallbackUrl={preview.title.posterFallbackUrl}
              sizes={CASE_SIZES}
              priority
              layout={false}
              lift={false}
            />
          </PerspectiveCover>
        </Reveal>

        <div className={COPY}>
          <Reveal order={4}>
            <p className="text-kicker text-ink-secondary">{t('share.preview.overline')}</p>
          </Reveal>
          <Reveal order={5}>
            <h1 className="text-balance text-display-sm text-ink sm:text-display">{name}</h1>
          </Reveal>
          {meta ? (
            <Reveal order={6}>
              <p className="text-subhead text-ink-secondary">{meta}</p>
            </Reveal>
          ) : null}

          {preview.kind === 'recommendation' && sharerName && preview.sharer ? (
            <Reveal order={7} className="mt-2 w-full">
              <div className="flex items-start gap-3 rounded-lg bg-surface p-4 text-left">
                <PaletteAvatar
                  name={sharerName}
                  avatarUrl={preview.sharer.avatarUrl}
                  palette={paletteFromTitle(preview.sharer.username)}
                  className="h-9 w-9 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-subhead font-medium text-ink">
                    {t('share.preview.recommendation', { name: sharerName, title: name })}
                  </p>
                  {preview.note ? (
                    <p className="mt-1 whitespace-pre-line break-words text-body text-ink-secondary">
                      {preview.note}
                    </p>
                  ) : null}
                </div>
              </div>
            </Reveal>
          ) : synopsis ? (
            <Reveal order={7}>
              <p className="prose-measure text-body text-ink-secondary">{synopsis}</p>
            </Reveal>
          ) : null}

          <Reveal order={8} className="mt-3 flex w-full flex-col gap-3">
            {status === 'loading' ? (
              <div className="flex h-12 items-center justify-center sm:justify-start" role="status">
                <Loader2 className="h-5 w-5 animate-spin text-ink-secondary" aria-hidden="true" />
                <span className="sr-only">{t('common.loading')}</span>
              </div>
            ) : isAuthenticated && targetPath ? (
              <Button asChild variant="accent" size="lg" className="sm:self-start">
                <Link to={targetPath}>{t('share.preview.openTitle')}</Link>
              </Button>
            ) : targetPath ? (
              <>
                <p className="text-subhead text-ink-secondary">{t('share.preview.joinPitch')}</p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button asChild variant="accent" size="lg">
                    <Link to={`/signup?next=${next}`}>{t('share.preview.createAccount')}</Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <Link to={`/login?next=${next}`}>{t('share.preview.logIn')}</Link>
                  </Button>
                </div>
              </>
            ) : null}
            <Button asChild variant="ghost" className="h-11 sm:-ml-3 sm:self-start">
              <a href={appSchemeLink(code)}>{t('share.preview.openInApp')}</a>
            </Button>
          </Reveal>
        </div>
      </article>
    </RevealGroup>
  );
}
